import * as constants from "./actionTypes";
import * as common from "./commonActions";
import { createAsyncThunk } from '@reduxjs/toolkit'

// import CONSENT_VERSIONS from '../constants/consentVersions';

import Axios from "../axios";
import { deserialize, serialize } from '../utility/serialization'
import { handleAPIError } from '../utility/actionHelper'
import { objectToFormData } from '../utility/formHelper'

export function successRequestingUserToken(response) {
  if (response.status === 200) {
    return { type: constants.SIGNIN_USER, user: response.data.user };
  } else {
    console.log("ERROR ON SIGNIN");
    return { type: constants.UI_ERROR, error: response.data };
  }
}

export const successConfirmingUserEmail = (response) => {
  if (response.status === 200) {
    return { type: constants.SIGNIN_USER, user: response.data.user }
  } else {
    return { type: constants.UI_ERROR, error: response.data }
  }
}

export function successRequestingNewUser(response) {
  if (response.status === 201) {
    return { type: constants.SIGNIN_USER, user: response.data.user };
  } else {
    console.log("ERROR ON SIGNIN");
    return { type: constants.UI_ERROR, error: response.data };
  }
}

export function successRequestingNewFamily(response) {
  if (response.status === 201) {
    return { type: constants.ADD_USER, user: response.data.user };
  } else {
    // This don't make no sense-- why is it called "SUCCESSrequestingNewFamily" but it's handling an error? Anyway, the way these are set up, they'll never receive an error. They only ever receieve the success response.
    console.log("ERROR ON SIGNIN");
    return { type: constants.UI_ERROR, error: response.data };
  }
}
// Following the convention of this file, adding a general failure option
export const failure = (error) => ({ type: constants.UI_ERROR, error });

export function successSigningOutUser() {
  return { type: constants.SIGN_OUT };
}

export function successCreatingConsent(response) {
  if (response.status === 200) {
    return {
      type: constants.UPDATE_CURRENT_USER,
      current_user: response.data.user,
    };
  } else {
    console.log("ERROR REGISTERING CONSENT");
    return { type: constants.UI_ERROR, error: response.data };
  }
}

export function successRequestingGuardianConsent(response) {
  if (response.status === 200) {
    return { type: constants.GUARDIAN_CONSENT_REQUEST };
  } else {
    console.log("ERROR SENDING REQUEST TO GUARDIAN FOR CONSENT");
    return { type: constants.UI_ERROR, error: response.data };
  }
}

// THUNKS HERE
export function registerConsent(consents) {
  return function (dispatch) {
    dispatch(common.loadingChanged(true));

    return createConsent(consents)
      .then((response) => dispatch(successCreatingConsent(response)))
      .then(() => dispatch(common.loadingChanged(false)))
      .catch((err) => {
        dispatch(common.loadingChanged(false));
        throw err;
      });
  };
}

export function signInUser(email, password, communityId = null, invitationId = null) {
  return function (dispatch) {
    // BODY OF THUNK

    // TURN SPINNER ON...
    dispatch(common.loadingChanged(true));
    // FETCH API CALL FROM SERVER THEN DISPATCH UI ACTION
    // THEN TURN SPINNER OFF
    return requestGetToken(email, password, communityId, invitationId)
      .then((response) => { dispatch(successRequestingUserToken(response)); return response })
      .then((response) => { dispatch(common.loadingChanged(false)); return response })
      .catch((err) => {
        dispatch(common.loadingChanged(false));
        throw err;
      });
  };
}

export const confirmUserEmail = (confirmationToken) => (
  function (dispatch) {
    dispatch(common.loadingChanged(true))

    return requestConfirmUserEmail(confirmationToken)
      .then((response) => { dispatch(successConfirmingUserEmail(response)); return response })
      .then((response) => { dispatch(common.loadingChanged(false)); return response })
      .catch((err) => {
        dispatch(common.loadingChanged(false))
        throw err
      })
  }
)

export const resendConfirmationEmail = (email) => (
  function (dispatch) {
    dispatch(common.loadingChanged(true))

    return requestResendConfirmationEmail(email)
      .then((response) => { dispatch(common.loadingChanged(false)); return response })
      .catch((err) => {
        dispatch(common.loadingChanged(false))
        throw err
      })
  }
)

export const fetchIdpAuthorizationUrl = () => Axios.get('/api/v1/idp_authorization').then(res => deserialize(res.data).authorizationUri)
export const fetchIdpSignUpUrl = () => Axios.get('/api/v1/idp_sign_up').then(res => deserialize(res.data).signUpUri)

export function oauthLogin(code, state, mobile = false) {
  return function (dispatch) {
    return requestOAuthLogin(code, state, mobile)
      .then((response) => { dispatch(successRequestingUserToken(response)); return response })
  }
}

export function tokenAuth(token) {
  return function (dispatch) {
    dispatch(common.loadingChanged(true))
    return getUserFromToken(token)
      .then((response) => {
        dispatch(successRequestingUserToken(response))
        dispatch(common.loadingChanged(false))
      })
      .catch(() => {
        dispatch(common.loadingChanged(false))
      })
  }
}

export function signInSuperUser(data) {
  return function (dispatch) {
    // BODY OF THUNK

    // TURN SPINNER ON...
    dispatch(common.loadingChanged(true));
    // FETCH API CALL FROM SERVER THEN DISPATCH UI ACTION
    // THEN TURN SPINNER OFF
    return getSuperUserToken(data)
      .then((response) => dispatch(successRequestingUserToken(response)))
      .then((turnOffSpinner) => dispatch(common.loadingChanged(false)))
      .catch((err) => {
        dispatch(common.loadingChanged(false));
        throw err;
      });
  };
}

export function signOutUser() {
  return function () {
    return requestLogout()
      .then((response) => {
        const logOutUrl = `${response.data?.logout_url}${process.env.REACT_APP_BASE_URL}/post-logout`
        window.location.href = logOutUrl
      })
      .catch(() => {
        window.location = '/post-logout'
      })
  }
}

export const createUser = createAsyncThunk(
  'authentication/createUser',
  async (user, { rejectWithValue }) => {
    try {
      const userFormData = objectToFormData(serialize(user))

      const { data } = await registerUser(userFormData)

      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export const inviteFamilyMember = createAsyncThunk(
  'authentication/inviteFamilyMember',
  async (user, { rejectWithValue }) => {
    try {
      const userFormData = objectToFormData(serialize(user))

      const { data } = await registerInviteFamilyMember(userFormData)

      return deserialize(data)
    } catch (error) {
      return handleAPIError(error, rejectWithValue)
    }
  }
)

export function requestGuardianConsent(userId) {
  return function (dispatch) {
    dispatch(common.loadingChanged(true));
    return sendGuardiantConsentRequest(userId)
      .then((response) => dispatch(successRequestingGuardianConsent(response)))
      .then(() => dispatch(common.loadingChanged(false)));
  };
}

// API CALLS
function registerUser(user) {
  const requestUrl = '/api/v1/student/junior_golfers'

  return Axios.post(requestUrl, user)
}

function registerInviteFamilyMember(user) {
  const requestUrl = '/api/v1/student/invitations'

  return Axios.post(requestUrl, user)
}

const requestConfirmUserEmail = (confirmationToken) => {
  const requestUrl = '/api/v1/users/confirm_email'

  return Axios.post(requestUrl, serialize({ confirmationToken }))
}

const requestResendConfirmationEmail = (email) => {
  const requestUrl = '/api/v1/users/resend_confirmation_email'

  return Axios.post(requestUrl, { email })
}

const requestOAuthLogin = (code, state, mobile = false) => {
  const requestUrl = '/api/v1/users/oauth_login.json'

  const origin = state?.origin
  const accountSegment = state?.segment
  const communityId = state?.communityId
  const utmParams = state ? {
    utm_source: state.utmSource,
    utm_campaign: state.utmCampaign,
    utm_medium: state.utmMedium,
    utm_term: state.utmTerm,
    utm_content: state.utmContent,
    utm_id: state.utmId,
  } : {}

  return Axios.post(requestUrl, serialize({ code, mobile, origin, accountSegment, InvitationCommunityId: communityId, ...utmParams }))
}

function requestGetToken(email, password, communityId, invitationId) {
  const requestUrl = '/api/v1/users/get_token.json'
  const params = {
    auth: { email, password },
    ...(communityId && { invitation_community_id: communityId }),
    ...(invitationId && { invitation_id: invitationId }),
  }

  return Axios.post(requestUrl, params)
}

function getUserFromToken(token) {
  let requestUrl = '/api/v1/users/token_authentication'

  return Axios.post(requestUrl, {token})
}

function getSuperUserToken(data) {
  let requestUrl = "/api/v1/users/get_token.json";
  return Axios.post(requestUrl, {
    community_id: data.id,
  });
}

function createConsent(consents) {
  return Axios.post("/api/v1/consents", consents);
}

function sendGuardiantConsentRequest(userId) {
  return Axios.post("/api/v1/consents/get_guardian_consent", userId);
}

function requestLogout() {
  return Axios.delete('/api/v1/users/log_out')
}
